import { useEffect, useRef, useState } from "react";
import {
  RequestApproveUserRequest,
  RequestApproveUserRequestWrapper,
  RequestDeleteUserRequest,
  RequestDeleteUserRequestWrapper,
  RequestGetUserListRequest,
  RequestUpdateUserRequest,
  ResponseGetUserListResonse,
} from "../../services/OpenAPI";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { Permission } from "../../constants/roles";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { useApiGetUserList } from "../api/useApiGetUserList";
import { AdministratorListItem } from "../../components/Table/AdministratorListTable";
import { useApiUpdateUser } from "../api/useApiUpdateUser";
import { userUpdatedFlagState } from "../../recoil/atoms/updateUserAtoms";
import { useApiApproveUser } from "../api/useApiApproveUser";
import { useApiDeleteUser } from "../api/useApiDeleteUser";
import { RequestCreateMstUsersRequest } from "../../services/OpenAPI";
import { useApiRegisterUser } from "../api/useApiRegisterUser";
import { loginUserAtom } from "../../recoil/atoms/loginUserAtoms";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const useManagementAdmins = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const loginUser = useRecoilValue(loginUserAtom);
  const { fetchUserList } = useApiGetUserList();
  const { updateUser } = useApiUpdateUser();
  const { approveUsers } = useApiApproveUser();
  const { deleteUsers } = useApiDeleteUser();
  const loginUsersSelect = useRecoilValue(loginUserSelector);
  const setUserUpdatedFlag = useSetRecoilState(userUpdatedFlagState);
  const userUpdatedFlag = useRecoilValue(userUpdatedFlagState);
  const reserUserUpdatedFlag = useResetRecoilState(userUpdatedFlagState);
  const [addAdminDialogOpen, setAddAdminDialogOpen] = useState(false);

  const [userList, setUserList] = useState<AdministratorListItem[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<AdministratorListItem | null>(
    null
  );
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const handleCheckChange = (id: string) => {
    setCheckedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const { registerUser } = useApiRegisterUser();

  // 管理者追加処理
  const handleRegister = async () => {
    try {
      await loadUsers();
      setToastMessage("管理者を追加しました");
      setToastSeverity("success");
      setAddAdminDialogOpen(false);
      // 更新処理（例: 再取得）
      // await fetchAdmins();
    } catch (error) {
      setToastMessage("管理者の追加に失敗しました");
      setToastSeverity("error");
    } finally {
      setToastOpen(true);
    }
  };

  const handleCheckChangeAll = (newCheckedIds: Set<string>) => {
    setCheckedIds(newCheckedIds);
  };

  const transformData = (
    userList: ResponseGetUserListResonse[] | undefined
  ): AdministratorListItem[] => {
    if (!Array.isArray(userList)) return [];
    const result: AdministratorListItem[] = [];
    userList.forEach((user) => {
      const baseIds =
        user.baseInfo?.map((b) => b.id).filter((id): id is number => !!id) ??
        [];
      const baseNames =
        user.baseInfo
          ?.map((b) => b.name)
          .filter((name): name is string => !!name) ?? [];

      result.push({
        id: Number(user.id),
        userNumber: user.userNumber,
        lastName: user.lastName,
        firstName: user.firstName,
        mailAddress: user.mail,
        base: baseNames.join(", "),
        baseIds: baseIds,
        roomId: user.chatworkRoomId,
      });
    });

    return result;
  };

  const handleUpdate = async (
    updatedUser: AdministratorListItem,
    baseIds: number[]
  ) => {
    if (
      !loginUsersSelect ||
      !loginUsersSelect.tenantId ||
      !loginUsersSelect.baseId ||
      !loginUsersSelect.id ||
      !loginUsersSelect.permissionId
    )
      return;

    const request: RequestUpdateUserRequest = {
      permissionId: loginUsersSelect.permissionId,
      tenantId: loginUsersSelect.tenantId,
      id: Number(updatedUser.id),
      lastName: updatedUser.lastName,
      firstName: updatedUser.firstName,
      mail: updatedUser.mailAddress,
      baseId: Number(updatedUser.baseId),
      chatworkRoomId: updatedUser.roomId,
      baseIds: baseIds,
    };
    const result = await updateUser(request);
    if (result) {
      setToastMessage("更新が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("更新に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleRowClick = async (user: AdministratorListItem) => {
    setSelectedRow(user);
    setDetailDialogOpen(true);
  };

  const handleApprove = async () => {
    if (checkedIds.size === 0) return;

    // 選択されたユーザー情報をまとめる
    const selectedUsers = userList.filter((user) =>
      checkedIds.has(String(user.id))
    );

    // APIのリクエスト形式にマッピング
    const requestPayload: RequestApproveUserRequest[] = selectedUsers.map(
      (user) => ({
        id: Number(user.id),
        tenantId: loginUsersSelect?.tenantId,
        updatedUserId: loginUsersSelect?.id,
        baseId: user.baseId,
      })
    );
    const requestPayloadWrapper: RequestApproveUserRequestWrapper = {
      req: requestPayload,
    };

    const result = await approveUsers(requestPayloadWrapper);
    if (result) {
      setToastMessage("承認が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("承認に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleCancel = () => {
    setCheckedIds(new Set());
  };

  const handleDelete = async () => {
    if (checkedIds.size === 0) return;

    // 選択されたユーザー情報をまとめる
    const selectedUsers = userList.filter((user) =>
      checkedIds.has(String(user.id))
    );

    // APIのリクエスト形式にマッピング
    const requestPayload: RequestDeleteUserRequest[] = selectedUsers.map(
      (user) => ({
        id: Number(user.id),
        tenantId: loginUsersSelect?.tenantId,
        updatedUserId: loginUsersSelect?.id,
        baseId: user.baseId,
      })
    );

    const requestPayloadWrapper: RequestDeleteUserRequestWrapper = {
      req: requestPayload,
    };

    const result = await deleteUsers(requestPayloadWrapper);
    if (result) {
      setToastMessage("管理者削除に成功しました");
      setToastSeverity("success");
      setToastOpen(true);
      setCheckedIds(new Set());
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("管理者削除に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const fetchData = async (req: RequestGetUserListRequest) => {
    const response = await fetchUserList(req);
    if (response) {
      const transformed = transformData(response);
      setUserList(transformed);
    }
  };

  const loadUsers = async () => {
    if (
      loginUsersSelect?.tenantId &&
      loginUsersSelect.baseId &&
      loginUsersSelect?.id
    ) {
      const commonParams = {
        tenantId: loginUsersSelect.tenantId,
        baseId: loginUsersSelect.baseId,
        userId: loginUsersSelect.id,
      };

      const permissionIds = [Permission.BaseAdmin, Permission.BaseUser];
      const results = await Promise.all(
        permissionIds.map(async (permissionId) => {
          const response = await fetchUserList({
            ...commonParams,
            permissionId,
          });
          return response ? transformData(response) : [];
        })
      );

      const merged = results.flat();
      setUserList(merged);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loginUsersSelect]);

  useEffect(() => {
    if (userUpdatedFlag) {
      loadUsers();
      reserUserUpdatedFlag();
      setDetailDialogOpen(false);
    }
  }, [userUpdatedFlag]);

  return {
    inputRef,
    userList,
    selectedRow,
    checkedIds,
    handleCheckChange,
    handleUpdate,
    handleRowClick,
    detailDialogOpen,
    setDetailDialogOpen,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    handleApprove,
    handleCancel,
    handleDelete,
    handleCheckChangeAll,
    handleRegister,
    addAdminDialogOpen,
    setAddAdminDialogOpen,
  };
};
